// src/router/index.ts
// Router robusto con guard liviano, timeout en verificación y redirecciones por rol

import "../components/loader-component";
import { verificarRuta } from "../services/auth-service";
import { showLoader, hideLoader } from "../utils/loader";

type RouteModule = { default: typeof HTMLElement };

// ================= Utils hash/path =================
function getHashRaw(): string {
  // incluye query, ej: "/clients/offers/news?oferta=1891"
  return window.location.hash.slice(1);
}
function getHashPath(): string {
  // solo path, sin query, ej: "/clients/offers/news"
  const raw = getHashRaw();
  return (raw.split("?")[0]) || "/";
}

const PROTECTED_PREFIXES = ["/admin", "/clients", "/clientes", "/external"];
const LAST_GOOD_CLIENT_ROUTE = "__last_good_client_route__";

const isLoginPath = (p: string) => {
  const s = (p || "").startsWith("/") ? p.slice(1) : p;
  return s.toLowerCase().startsWith("login");
};
const isProtectedPath = (path: string): boolean => {
  const p = path.startsWith("/") ? path : "/" + path;
  if (isLoginPath(p)) return false;
  return PROTECTED_PREFIXES.some((pre) => p.startsWith(pre));
};

// ================= User helpers =================
function roleToUserType(role: string | null | undefined): "limited" | "full" | "admin" | "plex" | null {
  if (!role) return null;
  const r = String(role).toLowerCase();
  if (r === "admin" || r === "administrador") return "admin";
  if (r === "cliente_admin") return "full";
  if (r === "cliente_user") return "limited";
  if (r === "plex" || r === "externo_farmacia") return "plex";
  return null;
}

function getUserInfo() {
  let userObj: any = {};
  try { userObj = JSON.parse(localStorage.getItem("user") || "{}"); } catch { }
  const role: string | null = userObj?.rol ?? localStorage.getItem("role");
  let userType = (localStorage.getItem("user_type") as "limited" | "full" | "admin" | "plex" | null) || null;

  // Deducción y persistencia si faltaba
  const deduced = roleToUserType(role);
  if (!userType && deduced) {
    userType = deduced;
    try { localStorage.setItem("user_type", userType); } catch { }
  }

  // ✅ Detectar si la sesión se perdió (localStorage vacío pero intentamos acceder)
  const hasSessionData = !!(localStorage.getItem("auth_token") || localStorage.getItem("user"));

  return { role: role || "", userType, userObj, hasSessionData };
}

// Endurecido: usa getUserInfo + roleToUserType y persiste si faltaba
const isAdminUser = (): boolean => {
  const { role, userType } = getUserInfo();
  const finalType = userType ?? roleToUserType(role);
  if (!userType && finalType) { try { localStorage.setItem("user_type", finalType); } catch { } }
  return finalType === "admin";
};

function getHomeRoute(userType: "limited" | "full" | "admin" | "plex" | null): string {
  switch (userType) {
    case "admin": return "/admin";
    case "plex": return "/external/plex";
    case "full": return "/clients/home";
    case "limited": return "/login";
    default: return "/login";
  }
}

function canUserAccessRoute(userType: "limited" | "full" | "admin" | "plex" | null, path: string): boolean {
  if (path.startsWith("/external/plex")) return userType === "plex" || userType === "admin";
  if (path.startsWith("/admin")) return userType === "admin";
  if (!userType) return path.startsWith("/clients");
  if (userType === "full") return path.startsWith("/clients");
  if (userType === "limited") {
    const exact = new Set<string>([
      "/clients/cuenta-corriente",
      "/clients/comprobantes",
      "/clients/resumenes",
      "/clients/recibos",
      "/clients/pagos",
      "/clients/acreditacion-tarjetas",
      "/clients/reclamos/mis",
      "/clients/contact",
      "/clients/account",
    ]);
    if (exact.has(path)) return true;
    const prefixes = [
      "/clients/pedidos",
      "/clients/offers",
      "/clients/laboratorios",
      "/clients/combo",
      "/clients/contact",
      "/clients/account",
      "/clients/mis-ajustes",
    ];
    return prefixes.some((pre) => path.startsWith(pre));
  }
  return path.startsWith("/clients");
}

// ================= Rutas principales =================
export const mainRoutes: Record<string, () => Promise<{ default: typeof HTMLElement }>> = {
  "/login": () => import("../login/login-page"),
  "/login-new-pass": () => import("../login/login-new-pass"),
  "/clients": () => import("../clients/client-page"),
  "/admin": () => import("../admin/admin-page"),
  "/external": () => import("../admin/plex/plex-page"),
};


// ================= Carga dinámica de clients =================
const dynamicImports = import.meta.glob("../clients/**/**-page.ts");
const internalModules = dynamicImports as Record<string, () => Promise<RouteModule>>;

export const clientRoutes: Record<string, () => Promise<RouteModule>> = Object.fromEntries(
  Object.entries(internalModules).map(([path, importFn]) => {
    const route = path
      .replace("../clients", "/clients")
      .replace(/\/offers\/ahorrofarma\/ahorrofarma-page\.ts$/, "/offers/ahorrofarma")
      .replace(/\/ahorro\/ahorrofarma-page\.ts$/, "/offers/ahorrofarma")
      .replace(/\/home\/home-page\.ts$/, "/home")
      .replace(/\/cart\/cart-page\.ts$/, "/cart")
      .replace(/\/transfers\/transfers-page\.ts$/, "/transfers")
      .replace(/\/transfers\/laboratorio\/laboratorio-page\.ts$/, "/transfers/laboratorio/:id")
      .replace(/\/offers\/lanzamientos\/ofertas-lanzamientos-page\.ts$/, "/offers/releases")
      .replace(/\/offers\/medicamentos\/ofertas-medicamentos-page\.ts$/, "/offers/medicaments")
      .replace(/\/offers\/online\/ofertas-online-page\.ts$/, "/offers/online")
      .replace(/\/offers\/online\/combo-oferta\/combo-oferta-page\.ts$/, "/offers/combo-oferta/:id")
      .replace(/\/offers\/online\/oferta\/oferta-online-page\.ts$/, "/offers/online/:id")
      .replace(/\/offers\/ahorrofarma\/ofertas-ahorrofarma-page\.ts$/, "/offers/ahorrofarma")
      .replace(/\/offers\/novedades\/ofertas-novedades-page\.ts$/, "/offers/news")
      .replace(/\/offers\/perfumeria\/ofertas-perfumeria-page\.ts$/, "/offers/perfumeria")
      .replace(/\/account\/account-page\.ts$/, "/account")
      .replace(/\/combo\/combo-page\.ts$/, "/combo/:id")
      .replace(/\/contact\/contacto-page\.ts$/, "/contact")
      .replace(/\/reclamos\/reclamos-page\.ts$/, "/reclamos/nuevo")
      .replace(/\/reclamos\/mis-reclamos-page\.ts$/, "/reclamos")
      .replace(/\/pagos\/pagos-page\.ts$/, "/pagos")
      .replace(/\/pagos\/informar-pago-page\.ts$/, "/pagos/informar")
      .replace(/\/laboratorios\/laboratorios-page\.ts$/, "/laboratorios")
      .replace(/\/comprobantes\/comprobantes-page\.ts$/, "/comprobantes")
      .replace(/\/cuenta-corriente\/cuenta-corriente-page\.ts$/, "/cuenta-corriente")
      .replace(/\/resumenes\/resumenes-page\.ts$/, "/resumenes")
      .replace(/\/recibos\/recibos-page\.ts$/, "/recibos")
      .replace(/\/acreditacion-tarjetas\/acreditacion-tarjetas-page\.ts$/, "/acreditacion-tarjetas")
      .replace(/\/pedidos\/ver-pedidos-page\.ts$/, "/pedidos/ver")
      .replace(/\/pedidos\/enviar-pedidos-page\.ts$/, "/pedidos/enviar")
      .replace(/\/pedidos\/detalle-pedido-page\.ts$/, "/pedidos/detalle/:id")
      .replace(/\/revista\/revista-page\.ts$/, "/revista")
      .replace(/\/offers\/medicamentos\/revista\/revista-medicamentos-page\.ts$/, "/revista-medicamentos")
      .replace(/\/offers\/perfumeria\/revista\/revista-perfumeria-page\.ts$/, "/revista-perfumeria")
      .replace(/\/mis-ajustes\/mis-ajustes-page\.ts$/, "/mis-ajustes")
      .replace(/\.ts$/, "");
    return [route, importFn];
  })
);

// ================= Admin dinámico =================
const adminModules = import.meta.glob(
  "../admin/**/**-page.ts"
) as Record<string, () => Promise<RouteModule>>;

export const adminRoutes: Record<string, () => Promise<RouteModule>> = Object.fromEntries(
  Object.entries(adminModules).map(([path, importFn]) => {
    const route = path
      .replace("../admin", "/admin")
      .replace("/products/products-page.ts", "/products")
      .replace("/users/users-page.ts", "/users")
      .replace("/orders/orders-page.ts", "/orders")
      .replace("/settings/settings-page.ts", "/settings")
      .replace("/laboratorio/laboratorio-page.ts", "/laboratorio")
      .replace("/sidebar/catalogos/configurar-cards-page.ts", "/catalogos/configurar-cards");
    return [route, importFn];
  })
);

// ================= Routers =================
let _mainRouterRunning = false;

export async function adminRouter(): Promise<void> {
  const adminPageElement = document.querySelector("admin-page") as any;
  if (adminPageElement?.onRouteChange) {
    try { adminPageElement.onRouteChange(); } catch (e) { console.error("[adminRouter] onRouteChange", e); }
  }
}

export async function internalRouter() {
  const raw = getHashRaw();                 // ej: "/clients/offers/news?oferta=1891"
  if (raw === "/unauthorized" || raw === "" || raw === "/login") return;

  const internalAppElement = document.querySelector("client-page #app");
  if (!internalAppElement) return;

  const pathNoQuery = raw.split("?")[0];    // ✅ solo path para el match
  const queryString = raw.split("?")[1] || "";

  let matchedRoute: string | null = null;
  const params: Record<string, string> = {};

  for (const routePattern in clientRoutes) {
    const regex = new RegExp(`^${routePattern.replace(/:\w+/g, "([^/]+)")}$`);
    const match = pathNoQuery.match(regex);
    if (match) {
      matchedRoute = routePattern;
      const keys = [...routePattern.matchAll(/:(\w+)/g)].map((m) => m[1]);
      keys.forEach((key, i) => (params[key] = match[i + 1]));
      break;
    }
  }

  if (!matchedRoute) {
    internalAppElement.innerHTML = "<not-found-page></not-found-page>";
    return;
  }

  // Loader delayed logic
  let loaderTimer: number | null = null;
  const showLoaderDelayed = () => {
    loaderTimer = setTimeout(() => showLoader(), 150) as any;
  };
  const clearLoaderDelayed = () => {
    if (loaderTimer) clearTimeout(loaderTimer);
    hideLoader();
  };

  try {
    showLoaderDelayed(); // Start timer, only show if > 150ms

    const importFn = clientRoutes[matchedRoute];
    const module = await importFn();
    const Component = module.default;

    const element = new Component();

    // (opcional) exponer query como atributo
    if (queryString) element.setAttribute("query", queryString);
    for (const [key, value] of Object.entries(params)) element.setAttribute(key, value);

    // ✅ OPTIMIZACIÓN: Reemplazar contenido directamente
    const appEl = internalAppElement as HTMLElement;

    // Cambiar contenido
    internalAppElement.innerHTML = "";
    internalAppElement.appendChild(element);

    // Fade-in opcional suave si se desea, pero sin forzar opacity 0 previo
    // appEl.style.opacity = "1"; // Asegurar visibilidad

    // ✅ Scroll to top después de que el componente se renderice
    // Siempre hacer scroll al top para todas las páginas (incluido home)
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 50);

    // Guarda la última ruta buena con query completo (raw)
    try { sessionStorage.setItem(LAST_GOOD_CLIENT_ROUTE, raw); } catch { }
  } catch (err) {
    console.error("Error cargando componente:", err);
  } finally {
    clearLoaderDelayed(); // Ensure loader is hidden/cancelled
  }
}

// ================= Guard de navegación (con timeout suave) =================
let __guardBusy = false;
let __guardTimer: number | null = null;

const withTimeout = <T>(p: Promise<T>, ms = 3000, fallback: T) =>
  Promise.race([p, new Promise<T>(r => setTimeout(() => r(fallback), ms))]);

async function runGuard() {
  if (__guardTimer) clearTimeout(__guardTimer);
  __guardTimer = setTimeout(async () => {
    const path = getHashPath(); // ✅ sin query

    if (isLoginPath(path) || !isProtectedPath(path)) return;
    if (__guardBusy) return;

    __guardBusy = true;
    try {
      // Permisivo en guard para no congelar UI; mainRouter hará el check fuerte
      const { hasSessionData } = getUserInfo();
      // Si no hay datos de sesión (token/user en storage), forzar fallo inmediato
      if (!hasSessionData) {
        console.warn("[guard] ⚠️ Sin datos de sesión en navegación interna - bloqueando");
        const target = `${window.location.origin}${window.location.pathname}#/login`;
        if (window.location.href !== target) window.location.replace(target);
        return;
      }

      const ok = await withTimeout(verificarRuta(), 3000, true);
      if (!ok) {
        const target = `${window.location.origin}${window.location.pathname}#/login`;
        if (window.location.href !== target) window.location.replace(target);
      }
    } catch {
      const target = `${window.location.origin}${window.location.pathname}#/login`;
      if (window.location.href !== target) window.location.replace(target);
    } finally {
      __guardBusy = false;
    }
  }, 80) as any;
}
window.addEventListener("hashchange", runGuard, { passive: true });
runGuard();

// ================= Router principal =================
let _mainRouterPendingHash: string | null = null;

export async function mainRouter(): Promise<void> {
  const currentHash = window.location.hash;
  if (_mainRouterRunning) {
    // Encolar la última ruta pedida mientras se está ejecutando
    _mainRouterPendingHash = currentHash;
    return;
  }
  _mainRouterRunning = true;
  try {
    await _mainRouterImpl();
    // Si el hash cambió mientras ejecutábamos, procesar la nueva ruta
    while (_mainRouterPendingHash !== null && _mainRouterPendingHash !== window.location.hash) {
      _mainRouterPendingHash = null;
      await _mainRouterImpl();
    }
    _mainRouterPendingHash = null;
  } finally {
    _mainRouterRunning = false;
  }
}

async function _mainRouterImpl(): Promise<void> {
  const rawHash = window.location.hash;

  // ✅ Si NO hay hash (URL vacía o sin #), siempre redirigir a #/login
  if (!rawHash || rawHash === "") {
    location.hash = "#/login";
    return;
  }

  const path = (rawHash.slice(1).split("?")[0]) || "/"; // sin query

  // ✅ CRÍTICO: Verificar token INMEDIATAMENTE antes de hacer CUALQUIER cosa
  const { hasSessionData } = getUserInfo();
  if (path !== "/login" && isProtectedPath(path) && !hasSessionData) {
    console.warn("[router] ⚠️ Sin sesión - bloqueando navegación y redirigiendo a login");
    location.hash = "#/login";
    return; // DETENER TODO - no cargar nada
  }

  const appElement = document.getElementById("root") as HTMLElement;
  if (!appElement) return;

  let isAuthenticated = false;

  if (path !== "/login") {
    if (isProtectedPath(path)) {
      if (path.startsWith("/clients/")) {
        try { sessionStorage.setItem(LAST_GOOD_CLIENT_ROUTE, getHashRaw()); } catch { } // guarda con query
      }
      // Check fuerte (acá sí podemos esperar) - SIN loader para evitar parpadeo
      try {
        isAuthenticated = await verificarRuta();
      } catch (e) {
        console.error("[router] Error verificando ruta:", e);
        isAuthenticated = false;
      }
    } else {
      isAuthenticated = true;
    }
  }

  // No autenticado → login
  if (!isAuthenticated) {
    if (path !== "/login") {
      location.hash = "#/login";
      return;
    }
    if (!document.querySelector("login-page")) {
      appElement.innerHTML = "";
      const { default: Login } = await mainRoutes["/login"]();
      appElement.appendChild(new Login());
    }
    return;
  }

  // Autenticado en /login → derivar por tipo (incluye deducido)
  if (isAuthenticated && path === "/login") {
    const info = getUserInfo();
    const finalType = info.userType ?? roleToUserType(info.role);
    const last = sessionStorage.getItem(LAST_GOOD_CLIENT_ROUTE);

    const dest =
      finalType === "admin"
        ? "/admin"
        : (last && canUserAccessRoute(finalType, last.split("?")[0]))
          ? last
          : getHomeRoute(finalType);

    location.hash = dest; // dispara hashchange → mainRouter se re-ejecuta limpiamente
    return;
  }

  // /unauthorized → llevar a home acorde
  if (path === "/unauthorized") {
    const info = getUserInfo();
    const dest = getHomeRoute(info.userType ?? roleToUserType(info.role));
    location.replace("#" + dest);
    return;
  }

  // /clients → redirección al home correspondiente
  if (path === "/clients") {
    const info = getUserInfo();
    const dest = getHomeRoute(info.userType ?? roleToUserType(info.role));
    location.hash = dest;
    return;
  }

  // Admin standard
  if (path.startsWith("/admin")) {
    const info = getUserInfo();
    const finalType = info.userType ?? roleToUserType(info.role);
    if (finalType !== "admin") {
      location.hash = getHomeRoute(finalType);
      return;
    }
    const existing = document.querySelector("admin-page");
    if (!existing) {
      appElement.innerHTML = "";
      const { default: Admin } = await mainRoutes["/admin"]();
      appElement.appendChild(new Admin());
      // admin-page lee la ruta en su constructor y renderiza directamente — no necesitamos adminRouter() aquí
    } else {
      adminRouter();
    }
    return;
  }

  // Plex external
  if (path.startsWith("/external/plex")) {
    const info = getUserInfo();
    const finalType = info.userType ?? roleToUserType(info.role);
    if (finalType === "admin") {
      // El admin puede ver páginas plex desde su propio layout
      const existing = document.querySelector("admin-page");
      if (!existing) {
        appElement.innerHTML = "";
        const { default: Admin } = await mainRoutes["/admin"]();
        appElement.appendChild(new Admin());
      } else {
        adminRouter();
      }
      return;
    }
    if (finalType !== "plex") {
      location.hash = getHomeRoute(finalType);
      return;
    }
    const existing = document.querySelector("plex-page");
    if (!existing) {
      appElement.innerHTML = "";
      const { default: PlexPageObj } = await mainRoutes["/external"]();
      appElement.appendChild(new PlexPageObj());
    }
    return;
  }

  // Clients
  if (path.startsWith("/clients/")) {
    const info = getUserInfo();
    const finalType = info.userType ?? roleToUserType(info.role);
    if (!canUserAccessRoute(finalType, path)) {
      location.hash = getHomeRoute(finalType);
      return;
    }
    if (!document.querySelector("client-page")) {
      appElement.innerHTML = "";
      const { default: Clients } = await mainRoutes["/clients"]();
      appElement.appendChild(new Clients());
      const mo = new MutationObserver((_, obs) => {
        if (document.querySelector("client-page #app")) {
          obs.disconnect();
          // internalRouter() ya es llamado por client-page.firstUpdated()
        }
      });
      mo.observe(appElement, { childList: true, subtree: true });
    } else {
      // No hacer scroll aquí - lo maneja internalRouter según la ruta
      internalRouter();
    }
    return;
  }

  // Estáticos
  const routeFn = mainRoutes[path];
  if (routeFn) {
    appElement.innerHTML = "";
    const { default: StaticComponent } = await routeFn();
    appElement.appendChild(new StaticComponent());
  } else {
    appElement.innerHTML = "<not-found-page></not-found-page>";
  }
}


// =============== Boot listeners ===============
let isRouterInitialized = false;
function initializeRouter() {
  if (!isRouterInitialized) {
    window.addEventListener("load", mainRouter);
    window.addEventListener("hashchange", mainRouter);
    isRouterInitialized = true;
  }
}
initializeRouter();

// ====== Helpers debug opcionales ======
export function debugRoutes() {
  console.log("clientRoutes:", Object.keys(clientRoutes));
  console.log("adminRoutes:", Object.keys(adminRoutes));
}
(window as any).debugRoutes = debugRoutes;

export function lastGoodClientRoute() {
  return sessionStorage.getItem(LAST_GOOD_CLIENT_ROUTE);
}
(window as any).lastGoodClientRoute = lastGoodClientRoute;
